import { defineNuxtPlugin, useRuntimeConfig } from '#imports'

import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { isTestEnv } from '@backmarket/utils/env/isTestEnv'

import { useI18nLocale } from './composables/useI18nLocale'
import { useInjectTranslationsScriptInHead } from './composables/useInjectTranslationsScriptInHead'
import { useShouldLogMissingTranslationValue } from './composables/useShouldLogMissingTranslationValue'
import { useShowDefaultMessage } from './composables/useShowDefaultMessage'
import { useShowTranslationKeys } from './composables/useShowTranslationKeys'
import { NUXT_STATE_KEY, PROCESS_KEY } from './constants'
import type { I18n } from './types'
import { getI18n } from './utils/getI18n'
import { getRequestLocale } from './utils/getRequestLocale'

export type Injections = {
  i18n: I18n
}

export default defineNuxtPlugin<Injections>({
  name: 'i18n',
  setup: (nuxt) => {
    if (nuxt.ssrContext) {
      const locale = getRequestLocale(nuxt.ssrContext.event)

      if (!globalThis[PROCESS_KEY]) {
        throw new Error(
          '[nuxt-module-i18n] Cannot retrieve translations from the main process',
        )
      }

      // eslint-disable-next-line no-param-reassign
      nuxt.payload.state[NUXT_STATE_KEY] = {
        locale,
      }

      if (import.meta.server) {
        useInjectTranslationsScriptInHead(locale)
      }
    }

    const { i18n } = useRuntimeConfig().public

    if (
      !isTestEnv() &&
      !nuxt.payload.state[NUXT_STATE_KEY]?.locale &&
      !i18n.fallbackLocale
    ) {
      throw new Error('[nuxt-module-i18n] Cannot retrieve locale in useState')
    }

    const locale = useI18nLocale()
    const { market } = useMarketplace()
    const currencyCode = market.defaultCurrency
    const showDefaultMessage = useShowDefaultMessage()
    const showTranslationKeys = useShowTranslationKeys()
    const shouldLogMissingTranslationValue =
      useShouldLogMissingTranslationValue()

    return {
      provide: {
        i18n: getI18n(
          locale,
          currencyCode,
          showTranslationKeys,
          showDefaultMessage,
          shouldLogMissingTranslationValue,
        ),
      },
    }
  },
})
